import {
  getState,
  patchState,
  signalStore,
  withComputed,
  withHooks,
  withMethods,
  withState,
} from '@ngrx/signals';
import { computed, effect } from '@angular/core';

import { environment } from '../../../environments/environment';
import { Airport, AppState, Country } from '../models/app';
import { AccountInfo } from '../models/api/account';
import {
  GetAgencyContact,
  GetSelfInfoResponse,
  VCBCurrencyExchangeResponse,
} from '../models/api/common';
import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import dayjs from 'dayjs';

const initialState: AppState = {
  announceRef: null,
  reselectRef: null,
  auth: {
    authToken: '',
    currentUser: null,
  },
  common: {
    selfInfo: null,
    airports: [],
    countries: [],
    contactInfo: null,
    currencyExchange: null,
  },
};

export const AppStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withComputed(({ auth, common }) => ({
    _localAirportCodes: computed(() => [
      'HAN',
      'HPH',
      'VDO',
      'DIN',
      'SGN',
      'PQC',
      'VCS',
      'VCA',
      'CAH',
      'VKG',
      'DAD',
      'CXR',
      'DLI',
      'VII',
      'HUI',
      'THD',
      'BMV',
      'PXU',
      'UIH',
      'VDH',
      'TBB',
      'VCL',
    ]),
    airlinesLocal: computed(() => ['VN', 'VJ', 'QH', 'VU', 'SUN', 'LP']),
    airlinesInternational: computed(() => [
      'VN',
      'VJ',
      'QH',
      'TP',
      'AD',
      'AD2',
      'SJ',
      'SUN',
    ]),
    airlinesCombo: computed(() => ['VN', 'TP', 'AD', 'AD2', 'SJ']),

    isLoggedIn: computed(() => {
      return !!auth.currentUser();
    }),
    currencyExchangeUpdatedDate: computed(() => {
      const currencyExchange = common.currencyExchange();
      if (currencyExchange) {
        return dayjs(currencyExchange.UpdatedDate).toDate();
      }
      return dayjs().toDate();
    }),
  })),
  withMethods((store) => ({
    updateAnnounceRef(ref: NgbModalRef) {
      patchState(store, () => ({
        announceRef: ref,
      }));
    },

    updateReselectRef(ref: NgbModalRef) {
      patchState(store, () => ({
        reselectRef: ref,
      }));
    },

    checkLocalAirport(code: string) {
      return (
        store
          ._localAirportCodes()
          .findIndex((a) => a.toUpperCase() === code.toUpperCase()) !== -1
      );
    },

    getAirport(code: string) {
      return store.common.airports().find((a) => a.code === code);
    },

    updateSelfInfo(selfInfo: GetSelfInfoResponse) {
      patchState(store, (state) => ({
        common: {
          ...state.common,
          selfInfo: selfInfo,
        },
      }));
    },

    updateCountries(countries: Array<Country>) {
      patchState(store, (state) => ({
        common: {
          ...state.common,
          countries,
        },
      }));
    },

    updateAirports(airports: Array<Airport>) {
      patchState(store, (state) => ({
        common: {
          ...state.common,
          airports,
        },
      }));
    },

    updateUserLogin(user: AccountInfo | null) {
      patchState(store, (state) => ({
        auth: {
          ...state.auth,
          currentUser: user,
        },
      }));
    },

    updateAuthToken(token: string) {
      patchState(store, (state) => ({
        auth: {
          ...state.auth,
          authToken: token,
        },
      }));
    },

    updateContact(value: GetAgencyContact) {
      patchState(store, (state) => ({
        common: {
          ...state.common,
          contactInfo: value,
        },
      }));
    },

    updateCurrencyExchange(value: VCBCurrencyExchangeResponse) {
      patchState(store, (state) => ({
        common: {
          ...state.common,
          currencyExchange: value,
        },
      }));
    },
  })),
  withHooks({
    onInit(store) {
      if (environment.debug) {
        effect(() => {
          const state = getState(store);
          console.log('[APP_STATE]', state);
        });
      }
    },
  }),
);
